// Animation management module for PPT Hero game
export default class AnimationManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Create and animate shapes in with staggered effect
  createAndAnimateShapesIn() {
    try {
      // Create shapes (initially invisible)
      this.scene.createLevelShapes();
      
      // Animate shapes in with staggered effect
      if (this.scene.allShapes && Array.isArray(this.scene.allShapes)) {
        this.scene.allShapes.forEach((shape, index) => {
          if (shape) {
            // Start invisible and scaled down
            shape.setAlpha(0);
            shape.setScale(0.3);
            
            // Stagger the entrance animation
            this.scene.time.delayedCall(index * 100, () => {
              this.scene.tweens.add({
                targets: shape,
                alpha: 1,
                scaleX: 1,
                scaleY: 1,
                duration: 400,
                ease: "Back.easeOut"
              });
            });
          }
        });
      }
      
    } catch (error) {
      console.error('Error creating and animating shapes in:', error);
    }
  }

  // Fade out all shapes with staggered effect
  fadeOutAllShapes() {
    try {
      if (this.scene.allShapes && Array.isArray(this.scene.allShapes)) {
        this.scene.allShapes.forEach((shape, index) => {
          if (shape && shape.alpha !== undefined) {
            // Stagger fade out for smooth effect
            this.scene.time.delayedCall(index * 30, () => {
              this.scene.tweens.add({
                targets: shape,
                alpha: 0,
                scaleX: 0.8,
                scaleY: 0.8,
                duration: 400,
                ease: "Sine.easeIn"
              });
            });
          }
        });
      }
      
    } catch (error) {
      console.error('Error fading out shapes:', error);
    }
  }

  // Animate shape selection
  animateShapeSelection(shape) {
    try {
      if (!shape) return;
      
      // Pulse animation for selection
      this.scene.tweens.add({
        targets: shape,
        scaleX: 1.1,
        scaleY: 1.1,
        duration: 200,
        ease: "Sine.easeOut",
        yoyo: true,
        repeat: 1
      });
      
    } catch (error) {
      console.error('Error animating shape selection:', error);
    }
  }

  // Animate shape deselection
  animateShapeDeselection(shape) {
    try {
      if (!shape) return;
      
      // Return to normal scale
      this.scene.tweens.add({
        targets: shape,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: "Sine.easeOut"
      });
      
    } catch (error) {
      console.error('Error animating shape deselection:', error);
    }
  }

  // Animate level completion
  animateLevelCompletion() {
    try {
      if (this.scene.allShapes && Array.isArray(this.scene.allShapes)) {
        this.scene.allShapes.forEach((shape, index) => {
          if (shape && shape.setFillStyle) {
            // Stagger the success glow effect
            this.scene.time.delayedCall(index * 50, () => {
              // Success glow effect
              shape.setFillStyle(0x00ff88, 0.8);
              this.scene.tweens.add({
                targets: shape,
                scaleX: 1.1,
                scaleY: 1.1,
                duration: 300,
                ease: "Back.easeOut",
                yoyo: true,
                onComplete: () => {
                  if (shape.originalFillColor !== undefined) {
                    shape.setFillStyle(shape.originalFillColor, 1.0);
                  }
                }
              });
            });
          }
        });
      }
      
    } catch (error) {
      console.error('Error animating level completion:', error);
    }
  }

  // Animate shape movement
  animateShapeMovement(shape, targetX, targetY, duration = 500) {
    try {
      if (!shape) return;
      
      this.scene.tweens.add({
        targets: shape,
        x: targetX,
        y: targetY,
        duration: duration,
        ease: "Power2"
      });
      
    } catch (error) {
      console.error('Error animating shape movement:', error);
    }
  }

  // Animate shape rotation
  animateShapeRotation(shape, targetRotation, duration = 500) {
    try {
      if (!shape) return;
      
      this.scene.tweens.add({
        targets: shape,
        rotation: targetRotation,
        duration: duration,
        ease: "Power2"
      });
      
    } catch (error) {
      console.error('Error animating shape rotation:', error);
    }
  }

  // Animate shape scaling
  animateShapeScaling(shape, targetScaleX, targetScaleY, duration = 500) {
    try {
      if (!shape) return;
      
      this.scene.tweens.add({
        targets: shape,
        scaleX: targetScaleX,
        scaleY: targetScaleY,
        duration: duration,
        ease: "Power2"
      });
      
    } catch (error) {
      console.error('Error animating shape scaling:', error);
    }
  }

  // Animate UI element entrance
  animateUIEntrance(element, delay = 0) {
    try {
      if (!element) return;
      
      // Start invisible and scaled down
      element.setAlpha(0);
      element.setScale(0.8);
      
      // Animate in after delay
      this.scene.time.delayedCall(delay, () => {
        this.scene.tweens.add({
          targets: element,
          alpha: 1,
          scaleX: 1,
          scaleY: 1,
          duration: 300,
          ease: "Back.easeOut"
        });
      });
      
    } catch (error) {
      console.error('Error animating UI entrance:', error);
    }
  }

  // Animate UI element exit
  animateUIExit(element, onComplete = null) {
    try {
      if (!element) return;
      
      this.scene.tweens.add({
        targets: element,
        alpha: 0,
        scaleX: 0.8,
        scaleY: 0.8,
        duration: 200,
        ease: "Back.easeIn",
        onComplete: onComplete
      });
      
    } catch (error) {
      console.error('Error animating UI exit:', error);
    }
  }

  // Animate notification entrance
  animateNotificationEntrance(notification) {
    try {
      if (!notification) return;
      
      // Start invisible and above target position
      notification.setAlpha(0);
      notification.setY(notification.y - 50);
      
      // Animate in
      this.scene.tweens.add({
        targets: notification,
        alpha: 1,
        y: notification.y + 50,
        duration: 300,
        ease: "Back.easeOut"
      });
      
    } catch (error) {
      console.error('Error animating notification entrance:', error);
    }
  }

  // Animate notification exit
  animateNotificationExit(notification, onComplete = null) {
    try {
      if (!notification) return;
      
      this.scene.tweens.add({
        targets: notification,
        alpha: 0,
        y: notification.y - 30,
        duration: 200,
        ease: "Back.easeIn",
        onComplete: onComplete
      });
      
    } catch (error) {
      console.error('Error animating notification exit:', error);
    }
  }

  // Animate score change
  animateScoreChange(element, oldValue, newValue) {
    try {
      if (!element) return;
      
      // Flash effect
      this.scene.tweens.add({
        targets: element,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 150,
        ease: "Sine.easeOut",
        yoyo: true,
        repeat: 1
      });
      
      // Color change animation
      if (newValue > oldValue) {
        element.setColor('#00ff88');
        this.scene.time.delayedCall(300, () => {
          element.setColor('#ffffff');
        });
      } else if (newValue < oldValue) {
        element.setColor('#ff4444');
        this.scene.time.delayedCall(300, () => {
          element.setColor('#ffffff');
        });
      }
      
    } catch (error) {
      console.error('Error animating score change:', error);
    }
  }

  // Stop all animations
  stopAllAnimations() {
    try {
      this.scene.tweens.killAll();
      console.log('All animations stopped');
      
    } catch (error) {
      console.error('Error stopping animations:', error);
    }
  }

  // Pause all animations
  pauseAllAnimations() {
    try {
      this.scene.tweens.pauseAll();
      console.log('All animations paused');
      
    } catch (error) {
      console.error('Error pausing animations:', error);
    }
  }

  // Resume all animations
  resumeAllAnimations() {
    try {
      this.scene.tweens.resumeAll();
      console.log('All animations resumed');
      
    } catch (error) {
      console.error('Error resuming animations:', error);
    }
  }

  // Smooth shape fade out with staggered effect
  fadeOutCurrentShapes() {
    if (this.scene.allShapes && Array.isArray(this.scene.allShapes)) {
      this.scene.allShapes.forEach((shape, index) => {
        if (shape && shape.alpha !== undefined) {
          // Stagger fade out for smooth effect
          this.scene.time.delayedCall(index * 30, () => {
            this.scene.tweens.add({
              targets: shape,
              alpha: 0,
              scaleX: 0.8,
              scaleY: 0.8,
              duration: 400,
              ease: "Sine.easeIn"
            });
          });
        }
      });
    }
  }
}
